import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import {
  refreshSession as cognitoRefresh,
  signOut,
} from '../components/tfa-auth/libs/cognito';

const baseURL = process.env.REACT_APP_API_SERVICE_URL || '';

const mutex = new Mutex();
const baseQuery = fetchBaseQuery({
  baseUrl: baseURL,
  prepareHeaders: async (headers, { endpoint }) => {
    // - uploadFile url uses a x-amz-security-token query param so we can't send an auth header as well without error
    if (endpoint === 'uploadFile') return headers;

    // On Smart Launch we need to send the JWT token in the header
    if (window.location.pathname.includes('smart-launch')) {
      const smartLaunchJWT = localStorage.getItem('smartLaunchJWT');
      if (smartLaunchJWT) headers.set('jwt', smartLaunchJWT);
    } else {
      const idToken = localStorage.getItem('idToken');
      if (idToken) headers.set('Authorization', `Bearer ${idToken}`);
    }

    return headers;
  },
});

const refreshSession = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token');
  try {
    return await cognitoRefresh(refreshToken);
  } catch (err) {
    console.log(err);
    throw err;
  }
};

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const { idToken } = (await refreshSession()) as {
          idToken?: { jwtToken?: string };
        };
        if (idToken && idToken.jwtToken) {
          localStorage.setItem('idToken', idToken.jwtToken);
          result = await baseQuery(args, api, extraOptions);
        }
      } catch {
        signOut();
        localStorage.clear();
        window.location.replace('/');
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};

const apiInstance = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  keepUnusedDataFor: 60 * 60,
  tagTypes: [
    'Users',
    'FavoriteCodes',
    'SavedSheets',
    'SearchResults',
    'Organizations',
    'FeeSchedules',
  ],
  endpoints: () => ({}),
});
export default apiInstance;
